import { useMemo } from "react";
import type { Category, Entry } from "@/types";
import type { ReportPeriod } from "@/utils/reportPeriod";
import {
  eachDayYmdInRange,
  eachMonthYmInRange,
  formatTrendAxisLabel,
} from "@/utils/reportPeriod";

export interface CategoryChartRow {
  name: string;
  value: number;
}

export interface TrendChartRow {
  label: string;
  expenses: number;
  gains: number;
}

function round2(n: number) {
  return Math.round(n * 100) / 100;
}

function buildCategoryData(entries: Entry[], categories: Category[]) {
  return categories
    .map((cat) => {
      const total = entries
        .filter((e) => e.categoryId === cat.id)
        .reduce((sum, e) => sum + Number(e.amount), 0);
      return { name: cat.name, value: round2(total) };
    })
    .filter((d) => d.value > 0);
}

/**
 * Groups by category for uncategorized rows, otherwise by (category, subcategory) pair.
 * Chart labels show only subcategory names to save space; if the same subcategory name
 * exists under multiple categories, labels include the category in parentheses.
 */
function buildSubcategoryData(entries: Entry[]) {
  const makeGroupKey = (cat: string, sub: string | undefined) =>
    sub ? `${cat}\0${sub}` : cat;

  const map = new Map<string, { cat: string; sub?: string }>();
  const totals = new Map<string, number>();

  for (const e of entries) {
    const cat = e.category?.name?.trim() || "-";
    const sub = e.subcategory?.name?.trim();
    const gk = makeGroupKey(cat, sub);
    totals.set(gk, (totals.get(gk) ?? 0) + Number(e.amount));
    if (!map.has(gk)) map.set(gk, { cat, ...(sub ? { sub } : {}) });
  }

  const rows = [...totals.entries()].map(([groupKey, value]) => {
    const meta = map.get(groupKey)!;
    return {
      cat: meta.cat,
      sub: meta.sub,
      value: round2(value),
    };
  });

  const categoriesPerSub = new Map<string, Set<string>>();
  for (const r of rows) {
    if (!r.sub) continue;
    if (!categoriesPerSub.has(r.sub)) categoriesPerSub.set(r.sub, new Set());
    categoriesPerSub.get(r.sub)!.add(r.cat);
  }

  return rows
    .map((r) => {
      const ambiguousSub =
        r.sub != null && (categoriesPerSub.get(r.sub)?.size ?? 0) > 1;

      const name =
        r.sub == null ? r.cat : ambiguousSub ? `${r.sub} (${r.cat})` : r.sub;

      return { name, value: r.value };
    })
    .filter((d) => d.value > 0)
    .sort((a, b) => b.value - a.value);
}

function buildTrendChartData(
  entries: Entry[],
  period: ReportPeriod,
  rangeStart: string,
  rangeEnd: string,
): TrendChartRow[] {
  const bucketKeys =
    period === "year"
      ? eachMonthYmInRange(rangeStart, rangeEnd)
      : eachDayYmdInRange(rangeStart, rangeEnd);

  const buckets: Record<string, { expenses: number; gains: number }> = {};
  for (const k of bucketKeys) {
    buckets[k] = { expenses: 0, gains: 0 };
  }

  for (const e of entries) {
    const day = e.spentAt;
    const key = period === "year" ? day.slice(0, 7) : day;
    if (!buckets[key]) continue;
    const amt = Number(e.amount);
    if ((e.kind ?? "expense") === "gain") buckets[key].gains += amt;
    else buckets[key].expenses += amt;
  }

  return bucketKeys.map((k) => ({
    label: formatTrendAxisLabel(period === "year" ? `${k}-01` : k, period),
    expenses: round2(buckets[k].expenses),
    gains: round2(buckets[k].gains),
  }));
}

export function useReportsAggregates(
  entries: Entry[],
  categories: Category[],
  period: ReportPeriod,
  rangeStart: string,
  rangeEnd: string,
) {
  return useMemo(() => {
    const expenseEntries = entries.filter(
      (e) => (e.kind ?? "expense") === "expense",
    );
    const gainEntries = entries.filter((e) => e.kind === "gain");

    const expenseCats = categories.filter(
      (c) => (c.kind ?? "expense") === "expense",
    );
    const gainCats = categories.filter((c) => c.kind === "gain");

    const categoryDataExpenses = buildCategoryData(expenseEntries, expenseCats);
    const categoryDataGains = buildCategoryData(gainEntries, gainCats);

    const subcategoryDataExpenses = buildSubcategoryData(expenseEntries);
    const subcategoryDataGains = buildSubcategoryData(gainEntries);

    const trendChartData = buildTrendChartData(
      entries,
      period,
      rangeStart,
      rangeEnd,
    );

    return {
      categoryDataExpenses,
      categoryDataGains,
      subcategoryDataExpenses,
      subcategoryDataGains,
      trendChartData,
    };
  }, [entries, categories, period, rangeStart, rangeEnd]);
}
